/**
 * 「前の画面へ」ボタンのための戻り先の受け渡し (2026-10-30)。
 *
 * 詳細画面 (いまは商談詳細) を開く入口で、今の画面の URL と名前を location.state に入れて渡す。
 * 行き先はそれがある時だけ「前の画面へ」を出し、押すと履歴を 1 つ戻る (ブラウザの戻ると同じ)。
 * 元の画面のタブ・選択・絞り込みは URL に入れておく (useUrlParam) ので、戻ると元の状態で開く。
 *
 * - state は履歴の項目に付くので、リロードしても残る。新しいタブで開いた時や URL 直打ちでは付かない (ボタンも出ない)
 * - 行き先の画面で URL を書き換える時は state を引き継ぐこと (useUrlParam は引き継ぐ)
 */
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface ReturnTo {
  /** 戻り先の pathname + search */
  path: string;
  /** ボタンに出す戻り先の名前 (例: 顧客詳細) */
  label: string;
}

export interface ReturnToState {
  returnTo: ReturnTo;
}

/** 入口側: 今の画面を戻り先にした state。Link の state / navigate の state に渡す。 */
export function useReturnToState(label: string): ReturnToState {
  const { pathname, search } = useLocation();
  return useMemo(
    () => ({ returnTo: { path: pathname + search, label } }),
    [pathname, search, label],
  );
}

function readReturnTo(state: unknown): ReturnTo | null {
  if (!state || typeof state !== 'object') return null;
  const r = (state as { returnTo?: unknown }).returnTo;
  if (!r || typeof r !== 'object') return null;
  const { path, label } = r as { path?: unknown; label?: unknown };
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return null;
  if (typeof label !== 'string' || !label) return null;
  return { path, label };
}

/** 行き先側: 戻り先 (無ければ null) と、戻る処理。 */
export function useReturnTo(): { returnTo: ReturnTo | null; goBack: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = readReturnTo(location.state);
  const path = returnTo?.path ?? null;

  const goBack = useCallback(() => {
    if (!path) return;
    // state は入口から push で来た時にだけ付くので、1 つ前の履歴が戻り先。
    // 前の履歴が無い時 (idx が 0 / 不明) だけ、記録した URL へ移動する。
    const idx = (window.history.state as { idx?: unknown } | null)?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate(path);
  }, [navigate, path]);

  return { returnTo, goBack };
}
